import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import HistoryToggleOffRoundedIcon from '@mui/icons-material/HistoryToggleOffRounded';
import {
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  CompactTablePagination,
  EmptyState,
  ErrorState,
  LoadingState,
  TablePageShell,
} from '@/shared/ui';

import { useStockLedger } from '../../hooks';
import { formatDateTime, formatSignedQuantity } from '../../lib/format';
import { useLocationLookup } from '../../lib/useLocationLookup';
import {
  ACTOR_TYPE_LABELS,
  MOVEMENT_TYPE_FILTER_OPTIONS,
  MOVEMENT_TYPE_LABELS,
  movementTypeColor,
  REFERENCE_TYPE_FILTER_OPTIONS,
  REFERENCE_TYPE_LABELS,
} from '../../model/options';
import type { StockMovementType, StockReferenceType } from '../../model/types';
import { LocationSelect } from './LocationSelect';
import { ProductIdCell } from './ProductIdCell';

export interface LedgerPanelProps {
  /** Seeds the product/location filters — set when arriving via a Stock-tab row's "Ledger" action. */
  initialProductId?: string;
  initialLocationId?: string;
}

/** Converts a `datetime-local` input value to a full ISO-8601 date-time, or undefined if blank/invalid. */
function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Tab 2 — the immutable, filterable stock movement ledger. */
export function LedgerPanel({ initialProductId, initialLocationId }: LedgerPanelProps) {
  const { t } = useTranslation('inventory');
  const { locations, getLocationName } = useLocationLookup();

  const [productId, setProductId] = useState(initialProductId ?? '');
  const [locationId, setLocationId] = useState(initialLocationId ?? '');
  const [movementType, setMovementType] = useState<StockMovementType | ''>('');
  const [referenceType, setReferenceType] = useState<StockReferenceType | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const query = useStockLedger({
    productId: productId.trim() || undefined,
    locationId: locationId || undefined,
    movementType: movementType || undefined,
    referenceType: referenceType || undefined,
    from: toIso(from),
    to: toIso(to),
    page,
    size,
  });
  const entries = query.data?.items ?? [];
  const total = query.data?.page?.totalElements ?? 0;
  const filtered = Boolean(
    productId.trim() || locationId || movementType || referenceType || from || to,
  );

  const resetPageAnd = (fn: () => void) => {
    fn();
    setPage(0);
  };

  return (
    <Stack spacing={2.5}>
      {/* Filter bar */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label={t('ledgerPanel.productIdLabel')}
          placeholder={t('ledgerPanel.productIdPlaceholder')}
          value={productId}
          onChange={(e) => resetPageAnd(() => setProductId(e.target.value))}
          sx={{ minWidth: 220 }}
          slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
        />
        <LocationSelect
          label={t('ledgerPanel.locationFilterLabel')}
          value={locationId}
          onChange={(value) => resetPageAnd(() => setLocationId(value))}
          locations={locations}
          allOptionLabel={t('ledgerPanel.allLocations')}
          sx={{ minWidth: 200 }}
        />
        <TextField
          select
          size="small"
          label={t('ledgerPanel.movementTypeLabel')}
          value={movementType}
          onChange={(e) =>
            resetPageAnd(() => setMovementType(e.target.value as StockMovementType | ''))
          }
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('ledgerPanel.allMovementTypes')}</MenuItem>
          {MOVEMENT_TYPE_FILTER_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {t(o.label)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('ledgerPanel.referenceTypeLabel')}
          value={referenceType}
          onChange={(e) =>
            resetPageAnd(() => setReferenceType(e.target.value as StockReferenceType | ''))
          }
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('ledgerPanel.allReferenceTypes')}</MenuItem>
          {REFERENCE_TYPE_FILTER_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {t(o.label)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="datetime-local"
          label={t('ledgerPanel.fromLabel')}
          value={from}
          onChange={(e) => resetPageAnd(() => setFrom(e.target.value))}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          type="datetime-local"
          label={t('ledgerPanel.toLabel')}
          value={to}
          onChange={(e) => resetPageAnd(() => setTo(e.target.value))}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 200 }}
        />
      </Stack>

      {/* Content states */}
      {query.isLoading ? (
        <LoadingState label={t('ledgerPanel.loading')} />
      ) : query.isError ? (
        <ErrorState
          title={t('ledgerPanel.loadError.title')}
          message={query.error?.message ?? t('common:error.unknown')}
          onRetry={() => void query.refetch()}
        />
      ) : entries.length === 0 ? (
        <EmptyState
          title={filtered ? t('common:empty.noResults') : t('ledgerPanel.empty.title')}
          description={
            filtered
              ? t('ledgerPanel.empty.filteredDescription')
              : t('ledgerPanel.empty.description')
          }
          icon={<HistoryToggleOffRoundedIcon />}
        />
      ) : (
        <TablePageShell
          footer={
            <CompactTablePagination
              count={total}
              page={page}
              rowsPerPage={size}
              rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(_e, next) => setPage(next)}
              onRowsPerPageChange={(e) => resetPageAnd(() => setSize(parseInt(e.target.value, 10)))}
              labelDisplayedRows={({ from: f, to: tt, count }) =>
                `${f}–${tt} ${t('common:pagination.of')} ${count}`
              }
            />
          }
        >
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('ledgerPanel.table.occurredAt')}</TableCell>
                  <TableCell>{t('ledgerPanel.table.product')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {t('ledgerPanel.table.location')}
                  </TableCell>
                  <TableCell>{t('ledgerPanel.table.movementType')}</TableCell>
                  <TableCell align="right">{t('ledgerPanel.table.quantityDelta')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {t('ledgerPanel.table.reference')}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {t('ledgerPanel.table.actor')}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {t('ledgerPanel.table.reason')}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {t('ledgerPanel.table.note')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(entry.occurredAt)}
                    </TableCell>
                    <TableCell>
                      <ProductIdCell productId={entry.productId} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Tooltip title={entry.locationId}>
                        <Typography variant="body2">{getLocationName(entry.locationId)}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={movementTypeColor(entry.movementType)}
                        label={t(MOVEMENT_TYPE_LABELS[entry.movementType] ?? entry.movementType)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          fontFamily: 'JetBrains Mono, monospace',
                          color: entry.quantityDelta >= 0 ? 'success.main' : 'error.main',
                        }}
                      >
                        {formatSignedQuantity(entry.quantityDelta)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2">
                        {t(REFERENCE_TYPE_LABELS[entry.referenceType] ?? entry.referenceType)}
                      </Typography>
                      {entry.referenceId ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                        >
                          {entry.referenceId}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2">
                        {t(ACTOR_TYPE_LABELS[entry.actorType] ?? entry.actorType)}
                      </Typography>
                      {entry.actorId ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                        >
                          {entry.actorId}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {entry.reasonCode ?? '—'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">
                        {entry.note ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TablePageShell>
      )}
    </Stack>
  );
}
